import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { LogIn, Mail, Lock, Eye, EyeOff, Loader2 } from "lucide-react";
import { getApiUrl } from "../config/api";

export default function Login() {
  const [email, setEmail] = useState(() => {
    // Load saved email from localStorage
    return localStorage.getItem('savedEmail') || '';
  });
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [emailFocused, setEmailFocused] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [keepSignedIn, setKeepSignedIn] = useState(() => {
    // Load keep signed in preference from localStorage
    return localStorage.getItem('keepSignedIn') === 'true';
  });
  const [rememberEmail, setRememberEmail] = useState(() => {
    // Load remember email preference from localStorage
    return localStorage.getItem('rememberEmail') === 'true';
  });
  const navigate = useNavigate();

  const login = async () => {
    if (!email || !password) {
      setError("Please enter both email and password");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const res = await axios.post(getApiUrl("/api/auth/login"), {
        email,
        password,
        rememberMe: keepSignedIn,
      });
      
      if (res.data.token) {
        localStorage.setItem("token", res.data.token);
        
        // Save email if remember email is checked
        if (rememberEmail) {
          localStorage.setItem('savedEmail', email);
        } else {
          // Clear saved email if unchecked
          localStorage.removeItem('savedEmail');
        }
        
        // Force a page reload to ensure authentication state is updated
        window.location.href = "/";
      } else {
        setError("No token received from server");
      }
    } catch (err) {
      console.error("Login error:", err);
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else if (err.response?.status === 401) {
        setError("Invalid email or password");
      } else {
        setError("Login failed. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      login();
    }
  };

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 flex flex-col">
      {/* Top row with both logos - hidden on mobile */}
      <div className="hidden md:block absolute top-8 left-0 right-0 flex items-center justify-between px-8 z-10">
        {/* KCI Logo on the left */}
        <div className="flex items-center space-x-3">
                  <img 
          src="/KCILogo.png" 
          alt="KCI Logo" 
          className="h-14 w-auto drop-shadow-sm"
        />
        </div>
        
                {/* MyTrailer Logo centered */}
        <div className="absolute left-1/2 transform -translate-x-1/2 max-w-80">
                  <img 
          src="/MyTrailerLogo.png" 
          alt="MyTrailer Logo"
          style={{ maxWidth: '100%', height: 'auto' }}
        />
        </div>
        
        {/* Empty div on the right to balance the layout */}
        <div className="w-32"></div>
      </div>

      {/* Mobile header - only visible on mobile */}
      <div className="md:hidden flex items-center justify-center py-4 px-4">
        <img 
          src="/MyTrailerLogo.png" 
          alt="MyTrailer Logo"
          className="h-12 w-auto"
        />
      </div>

      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-[#51380d]/5 to-[#3d2a0a]/5 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-[#51380d]/5 to-[#3d2a0a]/5 rounded-full blur-3xl"></div>
      </div>

      {/* Centered login form */}
      <div className="flex-1 flex items-center justify-center px-4 py-4 md:px-6 md:py-12 md:pt-32">
        
        <div className="w-full max-w-sm md:max-w-md">
          {/* Login Card */}
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-2xl border border-white/20 p-4 md:p-8">
            {/* Header */}
            <div className="text-center mb-6 md:mb-8">
              <h1 className="text-2xl md:text-3xl text-gray-900 mb-2">Welcome Back</h1>
              <p className="text-sm md:text-base text-gray-600">Sign in to your MyTrailer account</p>
            </div>
            
            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6 flex items-center space-x-2">
                <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                <span className="text-sm font-medium">{error}</span>
              </div>
            )}
            
            {/* Login Form */}
            <form onSubmit={(e) => { e.preventDefault(); login(); }} className="space-y-4 md:space-y-6">
              {/* Email Field */}
              <div className="relative">
                <div className="relative">
                  <Mail className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 transition-colors duration-200 ${
                    emailFocused ? 'text-[#51380d]' : 'text-gray-400'
                  }`} />
                  <input
                    className={`w-full pl-12 pr-4 py-3 md:py-4 border-2 rounded-xl transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-[#51380d]/20 ${
                      emailFocused 
                        ? 'border-[#51380d] bg-white' 
                        : 'border-gray-200 bg-gray-50 hover:bg-white hover:border-gray-300'
                    } ${error ? 'border-red-300 bg-red-50' : ''}`}
                    placeholder="Email address"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onFocus={() => setEmailFocused(true)}
                    onBlur={() => setEmailFocused(false)}
                    onKeyPress={handleKeyPress}
                    disabled={isLoading}
                  />
                </div>
              </div>

              {/* Password Field */}
              <div className="relative">
                <div className="relative">
                  <Lock className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 transition-colors duration-200 ${
                    passwordFocused ? 'text-[#51380d]' : 'text-gray-400'
                  }`} />
                  <input
                    className={`w-full pl-12 pr-12 py-3 md:py-4 border-2 rounded-xl transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-[#51380d]/20 ${
                      passwordFocused 
                        ? 'border-[#51380d] bg-white' 
                        : 'border-gray-200 bg-gray-50 hover:bg-white hover:border-gray-300'
                    } ${error ? 'border-red-300 bg-red-50' : ''}`}
                    placeholder="Password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onFocus={() => setPasswordFocused(true)}
                    onBlur={() => setPasswordFocused(false)}
                    onKeyPress={handleKeyPress}
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                    disabled={isLoading}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              {/* Keep Me Signed In Checkbox */}
              <div className="flex items-center justify-between">
                <label className="flex items-center space-x-2 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={keepSignedIn}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setKeepSignedIn(checked);
                      // Save preference to localStorage
                      localStorage.setItem('keepSignedIn', checked.toString());
                    }}
                    className="w-4 h-4 text-[#51380d] bg-gray-100 border-gray-300 rounded focus:ring-[#51380d] focus:ring-2"
                    disabled={isLoading}
                  />
                  <div className="flex flex-col">
                    <span className="text-sm text-gray-700 font-medium">Keep me signed in</span>
                    <span className="text-xs text-gray-500">
                      {keepSignedIn ? "Stay signed in for 30 days" : "Stay signed in for 1 day"}
                    </span>
                  </div>
                </label>
              </div>

              {/* Remember Email Checkbox */}
              <div className="flex items-center justify-between">
                <label className="flex items-center space-x-2 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={rememberEmail}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setRememberEmail(checked);
                      // Save preference to localStorage
                      localStorage.setItem('rememberEmail', checked.toString());
                      
                      // If unchecking, clear saved email
                      if (!checked) {
                        localStorage.removeItem('savedEmail');
                        setEmail('');
                      }
                    }}
                    className="w-4 h-4 text-[#51380d] bg-gray-100 border-gray-300 rounded focus:ring-[#51380d] focus:ring-2"
                    disabled={isLoading}
                  />
                  <div className="flex flex-col">
                    <span className="text-sm text-gray-700 font-medium">Remember email</span>
                    <span className="text-xs text-gray-500">
                      Save email for next time
                    </span>
                  </div>
                </label>
              </div>

              {/* Login Button */}
              <button
                type="submit"
                disabled={isLoading}
                className={`w-full py-3 md:py-4 px-6 rounded-xl font-semibold text-white transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98] focus:outline-none focus:ring-4 focus:ring-[#51380d]/30 ${
                  isLoading
                    ? "bg-gray-400 cursor-not-allowed shadow-none"
                    : "bg-[#b07135] hover:shadow-lg hover:shadow-[#51380d]/25"
                }`}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center space-x-2">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span>Signing In...</span>
                  </div>
                ) : (
                  "Sign In"
                )}
              </button>
            </form>
            
            {/* Additional Links */}
            <div className="mt-6 md:mt-8 space-y-3 md:space-y-4 text-center">
              <button
                onClick={() => navigate("/reset-request")}
                className="text-sm text-gray-600 hover:text-[#51380d] transition-colors duration-200 font-medium"
                disabled={isLoading}
              >
                Forgot your password?
              </button>
              
              <div className="pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-600 mb-2">Don't have an account?</p>
                <button
                  onClick={() => navigate("/signup")}
                  className="text-sm font-semibold text-[#51380d] hover:text-[#3d2a0a] transition-colors duration-200"
                  disabled={isLoading}
                >
                  Create an account
                </button>
              </div>
              
              <div className="pt-2">
                <a
                  href="/privacy"
                  className="text-xs text-gray-500 hover:text-[#51380d] transition-colors duration-200"
                >
                  Privacy Policy
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
